import { useState } from 'react';
import { Divider } from 'antd';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Notification, CustomIcon, OnlyInMobile, OnlyInDesktopTablet } from '@components/shared';
import Button from '@components/shared/Button';
import { CurrentUser, DeprecatedErrorResponse } from '@root/types';
import { AccountSettingsTab } from '@components/layouts/Truva/AccountSettings/AccountSettings';
import ProfessionalInfoForm from './ProfessionalInfoForm';
import TourbookInfoForm from './TourbookInfoForm';
import s from '../AccountSettings.module.less';

type Props = {
  currentUser: CurrentUser;
  currentTab: AccountSettingsTab;
  updateUser: (args: {
    user: Partial<CurrentUser>;
    onSuccess: () => void;
    onError: (errors: DeprecatedErrorResponse) => void;
    analyticsInfo: { source: string; action: string };
  }) => void;
  goBack: (AccountSettingsTab) => () => void;
  fetchCurrentUser: () => void;
};

const SettingsEdit = ({ currentUser, currentTab, goBack, updateUser, fetchCurrentUser }: Props) => {
  const [updating, setUpdatingState] = useState(false);
  const { t } = useTranslation('accountSettings');
  const [errors, setErrors] = useState<DeprecatedErrorResponse>();

  const handleSubmit = (values, analyticsInfo) => {
    setUpdatingState(true);
    updateUser({
      user: values,
      onSuccess: async () => {
        await fetchCurrentUser();
        setUpdatingState(false);
        Notification.info({ title: t`successfullyUpdatedAccount` });
        goBack(currentTab)(); // goBack to non-edit view
      },
      onError: errorsObject => {
        setUpdatingState(false);
        setErrors(errorsObject);
      },
      analyticsInfo,
    });
  };
  const getValidationStatusFor = fieldName =>
    errors?.invalidFields?.includes(fieldName) ? 'error' : undefined;

  const errorsSection =
    errors && !isEmpty(errors.messages) ? (
      <div className={s.formErrorsContainer}>
        {errors.messages.map(e => (
          <div className={s.formError} key={e}>
            <CustomIcon type="close" className={s.errorIcon} />
            {e}
          </div>
        ))}
      </div>
    ) : null;

  const headerTranslations = {
    professional_info: 'professionalInfoTitle',
    tourbook_contact_info: 'tourbookInfoTitle',
  };

  return (
    <>
      <OnlyInMobile>
        <Divider className={s.sectionDivider} />
      </OnlyInMobile>
      <div className={s.infoHeader}>
        <h2 className={s.professionalInfoTitle}>{t(headerTranslations[currentTab])}</h2>
        <Button
          className={s.editStateButton}
          disabled={updating}
          onClick={goBack(currentTab)}
          title={t('cancelButtonLabel')}
        >
          {t('cancelButtonLabel')}
        </Button>
      </div>
      <OnlyInDesktopTablet>
        <Divider className={s.sectionDivider} />
      </OnlyInDesktopTablet>
      {errorsSection}
      {currentTab === 'professional_info' && (
        <ProfessionalInfoForm
          currentUser={currentUser}
          handleSubmit={values =>
            handleSubmit(values, {
              action: 'update professional info',
              source: 'professional info tab',
            })
          }
          getValidationStatusFor={getValidationStatusFor}
        />
      )}
      {currentTab === 'tourbook_contact_info' && (
        <TourbookInfoForm
          currentUser={currentUser}
          handleSubmit={values =>
            handleSubmit(values, {
              action: 'update tourbook contact info',
              source: 'tourbook contact info tab',
            })
          }
        />
      )}
    </>
  );
};

export default SettingsEdit;
